/**
 * Page header with breadcrumb navigation and optional sidebar toggle.
 *
 * Reads breadcrumbs from the store (set by page components via useBreadcrumbs).
 * Web apps pass a toggleSlot for sidebar control.
 * Desktop apps omit it (toggle lives in title bar).
 */

import type { ReactNode } from "react";
import * as stylex from "@stylexjs/stylex";

import { chevronRight } from "@animus/iconic-svg";
import { Icon } from "@animus/primaris";

import { useBreadcrumbStore } from "../../capabilities/breadcrumbs.ts";
import { styles } from "./styles.stylex.ts";

export interface PageHeaderProps {
  toggleSlot?: ReactNode;
  onNavigate: (path: string) => void;
}

export function PageHeader({ toggleSlot, onNavigate }: PageHeaderProps) {
  const breadcrumbs = useBreadcrumbStore((s) => s.breadcrumbs);

  return (
    <header {...stylex.props(styles.container)}>
      {toggleSlot}
      {breadcrumbs.length > 0 && (
        <nav {...stylex.props(styles.breadcrumbs)} aria-label="Breadcrumb">
          {breadcrumbs.map((crumb, index) => {
            const { path } = crumb;

            return (
              <div {...stylex.props(styles.breadcrumbItem)} key={crumb.label}>
                {index > 0 && (
                  <Icon size="xs" svg={chevronRight} xstyle={styles.chevron} />
                )}
                {path !== undefined ? (
                  <button
                    {...stylex.props(styles.breadcrumbLink)}
                    onClick={() => onNavigate(path)}
                    type="button"
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span {...stylex.props(styles.breadcrumbCurrent)}>
                    {crumb.label}
                  </span>
                )}
              </div>
            );
          })}
        </nav>
      )}
    </header>
  );
}
